import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { HEADER_GAME_TYPES } from '@/Poker/data/Constants';
import { GameTypeHeader } from '@themed-components';
import { MyTourneyTile } from '@themed-components';
import { TourneySection } from '@themed-components';
import useGetMyTourneyDefinitionIds from '@/src/hooks/data/useGetMyTourneyDefinitionIds';
import useGetRecommendTourneyDefinitionIds from '@/src/hooks/data/useGetRecommendTourneyDefinitionIds';
import useGetUpdatedTourneyTabsList from '@/src/hooks/data/useGetUpdatedTourneyTabsList';
import useMasterTourneyDefinitionsIds from '@/src/hooks/data/useMasterTourneyDefinitionsIds';
import useTourneyLobby from '@/src/hooks/utils/useTourneyLobby';
import gameAssets from '@themed-assets';
import { TourneyTileSkeleton } from '@/src/components/TourneyTileSkeleton/TourneyTileSkeleton';
import { TabBarV2 } from '@themed-components';
import useTourneyFilterSortConfig from '@/src/hooks/data/useTourneyFilterSortConfig';
import usePersistedTabState from '@/src/hooks/data/usePersistedTabState';
import ActiveGamesButton from '@/src/components/ActiveGamesButton/ActiveGamesButton';
import ScrollToTopButton from '@/src/components/Common/ScrollToTopButton';
import useScrollToTop from '@/src/hooks/utils/useScrollToTop';

const GAMES = [
  {
    name: 'NLH',
    buyIn: '₹100',
    players: 259,
    maxPlayers: 20
  },
  {
    name: 'PLO4',
    buyIn: '₹100',
    players: 587,
    maxPlayers: 20
  }
];

const A23TourneyLobby = () => {
  const TABS = useGetUpdatedTourneyTabsList();
  const myTourneyIds = useGetMyTourneyDefinitionIds();
  const recommendedTourneyIds = useGetRecommendTourneyDefinitionIds();
  const tourneyLobby = useTourneyLobby();

  const [activeTab, setActiveTab] = usePersistedTabState(
    'tourneyActiveTab',
    {}
  );
  const [isLoading, setIsLoading] = useState(true);
  const [removeFilter, setRemoveFilter] = useState(false);

  const { scrollContainerRef, showScrollToTop, scrollToTop } = useScrollToTop(
    300,
    true
  );

  const transformedTabs = useMemo(() => {
    return TABS?.map((item) => ({
      name: item?.tabNameA23,
      gameType: item?.tabCode,
      tabCode: item?.tabCode,
      tabIcon: item?.tabIconA23
    }));
  }, [TABS]);

  const activeTabCode = useMemo(
    () => activeTab?.tabCode || '',
    [activeTab?.tabCode]
  );

  useEffect(() => {
    if (TABS.length === 0) return;

    const hasActiveTab =
      activeTab?.tabCode && Object.keys(activeTab).length > 0;

    if (!hasActiveTab) {
      const item = TABS[0];
      const defaultTab = {
        name: item?.tabNameA23,
        gameType: item?.tabCode,
        tabCode: item?.tabCode
      };

      setActiveTab(defaultTab);
    } else {
      const tabExists = TABS.some((tab) => tab?.tabCode === activeTab.tabCode);

      if (!tabExists) {
        const item = TABS[0];
        setActiveTab({
          name: item?.tabNameA23,
          gameType: item?.tabCode,
          tabCode: item?.tabCode
        });
      } else {
        console.log('Using persisted tab:', activeTab);
      }
    }
  }, [TABS, activeTab, setActiveTab]);

  const masterTourneyRegIds = useMasterTourneyDefinitionsIds(activeTabCode);

  useEffect(() => {
    if (masterTourneyRegIds !== undefined && masterTourneyRegIds.length > 0) {
      setIsLoading(false);
    }
  }, [masterTourneyRegIds]);

  const TOURNEY_STATUS_MAPPING = useMemo(
    () => [
      {
        key: 'ticketTourney',
        displayName: 'Tickets Available',
        method: tourneyLobby.ticketTourney,
        tagColor: '#f0745c'
      },
      {
        key: 'lateRegistration',
        displayName: 'Late Registration',
        method: tourneyLobby.lateRegistration,
        tagColor: '#f0745c'
      },
      {
        key: 'megaTourney',
        displayName: 'Featured Tournaments',
        method: tourneyLobby.megaTourney,
        tagColor: '#5f5f5f'
      },
      {
        key: 'startingInNext2Hours',
        displayName: 'Starting in the next 2 hours',
        method: tourneyLobby.startingInNext2Hours,
        tagColor: '#808b8e'
      },
      {
        key: 'startingLater',
        displayName: 'Starting Later',
        method: tourneyLobby.startingLater,
        tagColor: '#808b8e'
      },
      {
        key: 'announced',
        displayName: 'Announced',
        method: tourneyLobby.announced,
        tagColor: '#808b8e'
      },
      {
        key: 'live',
        displayName: 'Running',
        method: tourneyLobby.live,
        tagColor: '#eabf69'
      },
      {
        key: 'completedTourneys',
        displayName: 'Completed/Cancelled',
        method: tourneyLobby.completedTourneys,
        tagColor: '#5f5f5f'
      }
    ],
    [tourneyLobby]
  );

  const allTourneysEmpty = useMemo(() => {
    if (!masterTourneyRegIds) return true;

    return TOURNEY_STATUS_MAPPING.every((config) => {
      const tourneys = config.method
        ? config.method(masterTourneyRegIds)
        : config.data;
      return !tourneys || tourneys.length === 0;
    });
  }, [masterTourneyRegIds, TOURNEY_STATUS_MAPPING]);

  const onGameTabChange = useCallback(
    (tab) => {
      setActiveTab((prevTab) => {
        if (prevTab?.tabCode !== tab?.tabCode) {
          return tab;
        }
        return prevTab;
      });
    },
    [setActiveTab]
  );

  const anyFilters = useTourneyFilterSortConfig();

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="bg-background inset-0 min-h-full min-w-full pt-4">
          <TourneyTileSkeleton />
          <TourneyTileSkeleton />
          <TourneyTileSkeleton />
        </div>
      );
    }

    if (allTourneysEmpty) {
      const isDefaultFilter =
        anyFilters &&
        anyFilters.hasActiveFilters === false &&
        Array.isArray(anyFilters.gameType) &&
        anyFilters.gameType.length === 0 &&
        Array.isArray(anyFilters.tourneyStatus) &&
        anyFilters.tourneyStatus.length === 0 &&
        Array.isArray(anyFilters.buyIn) &&
        anyFilters.buyIn.length === 0 &&
        Array.isArray(anyFilters.format) &&
        anyFilters.format.length === 0 &&
        (anyFilters.sortBy === 'START_TIME_LOW_TO_HIGH' ||
          anyFilters.sortBy === undefined ||
          anyFilters.sortBy === '');

      if (isDefaultFilter) {
        return (
          <div className="rounded-400 mx-auto mt-10 max-w-md p-6 text-center">
            <div className="mb-2 flex w-full justify-center">
              <img
                src={gameAssets?.a23FallbackCards}
                alt="a23FallbackCards"
                style={{ height: 50, width: 50 }}
                className="opacity-30"
              />
            </div>
            <div className="mt-5 mb-2 text-sm font-bold">
              No tourneys available
            </div>
            <div className="text-secondary text-sm">
              There are no tournaments for this tab at the moment.
            </div>
          </div>
        );
      } else {
        return (
          <div className="rounded-400 mx-auto mt-10 max-w-md p-6 text-center">
            <div className="mb-2 flex w-full justify-center">
              <img
                src={gameAssets?.a23FallbackCards}
                alt="a23FallbackCards"
                style={{ height: 50, width: 50 }}
                className="opacity-30"
              />
            </div>
            <div className="mt-5 mb-2 text-sm font-bold">
              Oops! You cannot go all-in
            </div>
            <div className="text-secondary text-sm">
              There are no tourney games that match your suit.
            </div>
            <div className="text-secondary text-sm">
              Remove a few filters for better results
            </div>
            <div
              className="flex w-full justify-center"
              onClick={() => setRemoveFilter(true)}
            >
              <div className="from-success to-card-grad-green-700 text-primary rounded-300 mt-3 flex h-10 w-40 items-center justify-center bg-linear-to-b opacity-100">
                <div className="font-bold">Remove all filters</div>
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <div className="bg-background inset-0 min-h-full min-w-full pt-4">
        {TOURNEY_STATUS_MAPPING.map((config) => (
          <TourneySection
            key={config.key}
            config={config}
            activeTabCode={activeTabCode}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-background text-primary font-primary relative min-h-screen overflow-hidden">
      <GameTypeHeader type={HEADER_GAME_TYPES.TOURNEYS} />
      <div
        className="scroll-touch absolute top-15 right-0 bottom-0 left-0 overflow-auto overflow-y-auto"
        ref={scrollContainerRef}
      >
        {myTourneyIds.length > 0 && (
          <div
            className="no-scrollbar bg-header-bg flex flex-row gap-6 overflow-x-auto scroll-smooth p-3"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {myTourneyIds && myTourneyIds.length > 0 && (
              <div className="align-center flex flex-row items-center justify-center gap-3 text-center">
                <div>
                  <span className="text-h-secondary block text-sm font-medium">
                    My
                  </span>
                  <span className="text-h-secondary block text-sm font-medium">
                    Tournament
                  </span>
                </div>
                <div className="flex">
                  {myTourneyIds?.map((game, idx) => (
                    <MyTourneyTile key={idx} game={game} />
                  ))}
                </div>
              </div>
            )}

            {recommendedTourneyIds && recommendedTourneyIds.length > 0 && (
              <div className="align-center flex flex-row items-center justify-center gap-3 text-center">
                <div>
                  <span className="text-h-secondary block text-sm font-medium">
                    Recommended
                  </span>
                  <span className="text-h-secondary block text-sm font-medium">
                    Tourney
                  </span>
                </div>
                <div className="flex gap-4">
                  {GAMES.map((game, idx) => (
                    <MyTourneyTile key={idx} game={game} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          <div className="border-b-main sticky top-0 z-50 border-b">
            {TABS?.length > 0 && (
              <TabBarV2
                tabs={transformedTabs}
                activeTab={{ gameType: activeTab?.tabCode }}
                onTabChange={onGameTabChange}
                isTourney
                removeFilter={removeFilter}
                setRemoveFilter={setRemoveFilter}
              />
            )}
          </div>
          {renderContent()}
        </div>
      </div>

      <ActiveGamesButton />

      {showScrollToTop && (
        <ScrollToTopButton onClick={scrollToTop} visible={showScrollToTop} />
      )}
    </div>
  );
};

export default A23TourneyLobby;
